import dayjs, { Dayjs } from "dayjs";
import { IPayPeriodTemplate } from "@/business/business";
import { sortDateDescending } from "@/core/utils/date";
import { ISpiffApproval } from "@/spiff-approvals/spiff-approval";
import { PAYROLL_PERIOD_STATUS } from "@/spiffs/constants";
import {
  ISpiff,
  BaseSpiff,
  PayrollPeriod,
  PayrollPeriodStatus,
  GroupedPeriodSpiffs,
  GroupedSpiffsResult,
} from "@/spiffs/spiffs.d";
import { generatePayrollPeriods, getSpiffPeriod } from "./payroll-period-utils";

const getPeriodStatus = (
  periodStart: Dayjs,
  periodEnd: Dayjs,
): PayrollPeriodStatus => {
  const now = dayjs();
  if (now.isAfter(periodEnd, "day")) {
    return PAYROLL_PERIOD_STATUS.past;
  }
  if (now.isBefore(periodStart, "day")) {
    return PAYROLL_PERIOD_STATUS.future;
  }
  return PAYROLL_PERIOD_STATUS.current;
};

const getLegacyApprovals = (approvals: ISpiffApproval[]): ISpiffApproval[] =>
  approvals.filter(
    (approval) =>
      !approval.pay_period_start_date ||
      !approval.pay_period_inclusive_end_date,
  );

const getOldestUnlockedPeriod = (
  periods: PayrollPeriod[],
): PayrollPeriod | null => {
  for (let i = 0; i < periods.length; i++) {
    const period = periods[i];
    if (
      !period.isLocked &&
      (period.status === PAYROLL_PERIOD_STATUS.past ||
        period.status === PAYROLL_PERIOD_STATUS.current)
    ) {
      return period;
    }
  }
  return null;
};

/**
 * Groups spiffs by payroll period with rollover logic:
 * - Dismissed spiffs stay in their natural period
 * - Undismissed spiffs in approved periods roll to oldest unapproved period
 */
export const groupSpiffsByPayrollPeriod = (
  pendingSpiffs: Record<string, BaseSpiff[]>,
  dismissedSpiffs: Record<string, BaseSpiff[]>,
  approvals: ISpiffApproval[],
  template: IPayPeriodTemplate,
): GroupedSpiffsResult => {
  const legacyApprovals = getLegacyApprovals(approvals);
  const legacyApprovalIds = new Set(legacyApprovals.map((a) => a.id));
  const modernApprovals = approvals.filter(
    (approval) => !legacyApprovalIds.has(approval.id),
  );

  const periods = generatePayrollPeriods(template, 12, 4);
  const dismissedSpiffsFlat: ISpiff[] = Object.values(
    dismissedSpiffs,
  ).flat() as ISpiff[];

  const approvalsByPeriodId = new Map<string, ISpiffApproval>();
  const spiffsByApprovedPeriod = new Map<string, ISpiff[]>();

  // Create PayrollPeriod objects for each modern approval
  const approvalPeriods: PayrollPeriod[] = [];
  modernApprovals.forEach((approval) => {
    const periodId = `${approval.pay_period_start_date}_${approval.pay_period_inclusive_end_date}`;
    const periodStart = dayjs(approval.pay_period_start_date);
    const periodEnd = dayjs(approval.pay_period_inclusive_end_date);

    approvalsByPeriodId.set(periodId, approval);

    // Create a PayrollPeriod object for this approval if it doesn't exist in generated periods
    if (!periods.find((p) => p.id === periodId)) {
      const status = getPeriodStatus(periodStart, periodEnd);

      approvalPeriods.push({
        id: periodId,
        start: periodStart,
        end: periodEnd,
        displayRange: `${periodStart.format("MMM D")} - ${periodEnd.format("MMM D")}`,
        status,
        isLocked: true, // Approvals are always locked
      });
    }

    const nonDismissedSpiffs = (approval.spiffs || []) as ISpiff[];
    const dismissedInPeriod = dismissedSpiffsFlat.filter((spiff) => {
      const eventDate = dayjs(spiff.event_date);
      return (
        eventDate.isSameOrAfter(periodStart, "day") &&
        eventDate.isSameOrBefore(periodEnd, "day")
      );
    });

    const allSpiffsInPeriod = [...nonDismissedSpiffs, ...dismissedInPeriod];
    spiffsByApprovedPeriod.set(periodId, allSpiffsInPeriod);
  });

  // Add approval periods to the main periods array
  periods.push(...approvalPeriods);

  periods.forEach((period) => {
    if (approvalsByPeriodId.has(period.id)) {
      period.isLocked = true;
    }
  });

  // Mark empty periods as locked if there's any locked period newer than them
  // This prevents reinstating spiffs into periods that should be "past" a locked period
  const lockedPeriods = periods.filter((p) => p.isLocked);
  let newestLockedPeriod: PayrollPeriod | null = null;
  if (lockedPeriods.length > 0) {
    // Find the newest (most recent) locked period
    newestLockedPeriod = lockedPeriods.reduce(
      (newest, period) =>
        period.start.isAfter(newest.start) ? period : newest,
      lockedPeriods[0],
    );

    // Mark all periods older than the newest locked period as locked
    periods.forEach((period) => {
      if (period.start.isBefore(newestLockedPeriod!.start)) {
        period.isLocked = true;
      }
    });
  }

  const pendingSpiffsFlat: ISpiff[] = Object.values(
    pendingSpiffs,
  ).flat() as ISpiff[];

  const spiffsByPeriod = new Map<string, ISpiff[]>();
  const allPeriodsMap = new Map<string, PayrollPeriod>();

  periods.forEach((period) => {
    allPeriodsMap.set(period.id, period);
    if (spiffsByApprovedPeriod.has(period.id)) {
      spiffsByPeriod.set(period.id, spiffsByApprovedPeriod.get(period.id)!);
    } else {
      spiffsByPeriod.set(period.id, []);
    }
  });

  const unassignedSpiffs: ISpiff[] = [...pendingSpiffsFlat];

  dismissedSpiffsFlat.forEach((spiff) => {
    const spiffNaturalPeriod = getSpiffPeriod(spiff.event_date, template);
    if (!spiffsByApprovedPeriod.has(spiffNaturalPeriod.id)) {
      unassignedSpiffs.push(spiff);
    }
  });

  unassignedSpiffs.forEach((spiff) => {
    const calculatedNaturalPeriod = getSpiffPeriod(spiff.event_date, template);

    let naturalPeriod: PayrollPeriod;
    if (allPeriodsMap.has(calculatedNaturalPeriod.id)) {
      naturalPeriod = allPeriodsMap.get(calculatedNaturalPeriod.id)!;
    } else {
      naturalPeriod = calculatedNaturalPeriod;
      if (approvalsByPeriodId.has(naturalPeriod.id)) {
        naturalPeriod.isLocked = true;
      }

      // Also check if this period should be locked because it's older than any locked period
      if (
        newestLockedPeriod &&
        naturalPeriod.start.isBefore(newestLockedPeriod.start)
      ) {
        naturalPeriod.isLocked = true;
      }

      allPeriodsMap.set(naturalPeriod.id, naturalPeriod);
      spiffsByPeriod.set(naturalPeriod.id, []);
    }

    // Rollover: dismissed spiffs stay in natural period;
    // undismissed spiffs in locked periods roll to oldest unlocked period
    let targetPeriod = naturalPeriod;
    let targetPeriodId = naturalPeriod.id;

    if (naturalPeriod.isLocked && !spiff.dismissed_at) {
      // Get all periods from the map and convert to array sorted by start date
      const allPeriodsArray = Array.from(allPeriodsMap.values()).sort(
        (a, b) => {
          if (a.start.isBefore(b.start)) return -1;
          if (a.start.isAfter(b.start)) return 1;
          return 0;
        },
      );

      const oldestUnlocked = getOldestUnlockedPeriod(allPeriodsArray);
      if (oldestUnlocked) {
        targetPeriod = oldestUnlocked;
        targetPeriodId = oldestUnlocked.id;
      }
    }

    if (!allPeriodsMap.has(targetPeriodId)) {
      allPeriodsMap.set(targetPeriodId, targetPeriod);
      if (!spiffsByPeriod.has(targetPeriodId)) {
        spiffsByPeriod.set(targetPeriodId, []);
      }
    }

    const periodSpiffs = spiffsByPeriod.get(targetPeriodId);
    if (periodSpiffs && !spiffsByApprovedPeriod.has(targetPeriodId)) {
      periodSpiffs.push(spiff);
    }
  });

  const finalPeriods: PayrollPeriod[] = [...periods];
  allPeriodsMap.forEach((period) => {
    if (!periods.find((p) => p.id === period.id)) {
      finalPeriods.push(period);
    }
  });

  const groupedPeriods: GroupedPeriodSpiffs[] = finalPeriods.map((period) => {
    const spiffs = spiffsByPeriod.get(period.id) || [];
    const sortedSpiffs = [...spiffs].sort((a, b) =>
      sortDateDescending(a.event_date, b.event_date),
    );

    const pendingSpiffs = sortedSpiffs.filter((s) => !s.dismissed_at);
    const dismissedSpiffs = sortedSpiffs.filter((s) => !!s.dismissed_at);

    const uniqueUserIds = new Set(pendingSpiffs.map((s) => s.user_id));
    const userCount = uniqueUserIds.size;
    const totalAmount = pendingSpiffs.reduce((sum, s) => sum + s.amount, 0);
    const dismissedCount = dismissedSpiffs.length;
    const approval = approvalsByPeriodId.get(period.id);

    return {
      period,
      spiffs: sortedSpiffs,
      userCount,
      totalAmount,
      dismissedCount,
      isLocked: period.isLocked,
      approval,
    };
  });

  groupedPeriods.sort((a, b) => {
    if (a.period.start.isAfter(b.period.start)) return -1;
    if (a.period.start.isBefore(b.period.start)) return 1;
    return 0;
  });

  const periodsWithSpiffs = groupedPeriods.filter((grouped) => {
    const isPastOrCurrent =
      grouped.period.status === PAYROLL_PERIOD_STATUS.past ||
      grouped.period.status === PAYROLL_PERIOD_STATUS.current;
    const hasSpiffs = grouped.spiffs.length > 0;
    return isPastOrCurrent || hasSpiffs;
  });

  return {
    periodsWithSpiffs,
    legacyApprovals,
  };
};
